import { useIntl } from 'react-intl';
import { Col, Form, Input, Select } from 'antd';
import CpfFormField from '@/components/_common/CpfFormField';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import { InputDate, RgFormField } from '@/components/_common';
import { GenderType } from '@/core/domain/people/Customers/Customers.types';
import { isAppPortocar } from '@/index';
import { useCustomerFormContext } from '../CustomerFormProvider';
import validateCpf from '@/utils/functions/validateCpf';

interface Props {
  isEdit?: boolean;
}

function NaturalPersonFields(props: Props) {
  const form = Form.useFormInstance();
  const ruleRequired = useDefaultEmptyFieldError();
  const intl = useIntl();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const { customer } = useCustomerFormContext();

  const cpfCnpj = Form.useWatch('cpfCnpj', form);
  const foreignDocument = Form.useWatch('foreignDocument', form);

  const isValidCpf = validateCpf(customer?.cpfCnpj || '');

  return (
    <>
      {!foreignDocument && (
        <Col xs={24} sm={12} md={6}>
          <CpfFormField
            label="CPF"
            fieldName="cpfCnpj"
            required={isAppPortocar}
            disabled={isAppPortocar && !!props.isEdit && !!isValidCpf}
          />
        </Col>
      )}

      {!cpfCnpj && (
        <Col xs={24} sm={12} md={6}>
          <FloatingFormItem
            name="foreignDocument"
            label="Doc. identificação estrangeiro"
            rules={isAppPortocar && !cpfCnpj ? [ruleRequired] : []}
          >
            <Input
              disabled={
                !!props.isEdit && isAppPortocar && !!customer?.foreignDocument
              }
            />
          </FloatingFormItem>
        </Col>
      )}

      <Col xs={24} sm={24} md={12} span={12}>
        <FloatingFormItem
          name="name"
          label={intl.formatMessage({ id: 'customers.domain.name' })}
          rules={[ruleRequired, defaultMaxLenghtFieldError(100)]}
        >
          <Input />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={12} md={6}>
        <RgFormField
          label={intl.formatMessage({ id: 'customers.domain.rg' })}
        />
      </Col>

      <Col xs={24} sm={12} md={12} lg={6}>
        <FloatingFormItem
          name="municipalRegistration"
          label={intl.formatMessage({
            id: 'customers.domain.municipalRegistration',
          })}
          rules={[defaultMaxLenghtFieldError(15)]}
        >
          <Input />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={24} md={6}>
        <FloatingFormItem
          name="stateRegistration"
          label={intl.formatMessage({
            id: 'customers.domain.stateRegistration',
          })}
          rules={[defaultMaxLenghtFieldError(25)]}
        >
          <Input />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={12} md={12} lg={4}>
        <FloatingFormItem
          name="birthDate"
          label={intl.formatMessage({ id: 'customers.domain.birthDate' })}
          rules={isAppPortocar ? [ruleRequired] : []}
        >
          <InputDate />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={12} lg={4}>
        <FloatingFormItem name="creation" label="Data de Criação">
          <InputDate disabled />
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={12} md={12} lg={4}>
        <FloatingFormItem
          name="genderType"
          label={intl.formatMessage({ id: 'customers.domain.genderType' })}
        >
          <Select
            style={{ width: '100%' }}
            options={[
              {
                label: intl.formatMessage({
                  id: 'customers.domain.genderType.man',
                }),
                value: GenderType.Man,
              },
              {
                label: intl.formatMessage({
                  id: 'customers.domain.genderType.woman',
                }),
                value: GenderType.Woman,
              },
            ]}
          ></Select>
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={12} md={12} lg={8}>
        <FloatingFormItem
          name="maritalStatus"
          label={intl.formatMessage({ id: 'customers.domain.maritalStatus' })}
        >
          <Select
            style={{ width: '100%' }}
            options={[
              {
                label: intl.formatMessage({
                  id: 'customers.domain.maritalStatus.single',
                }),
                value: 'SINGLE',
              },
              {
                label: intl.formatMessage({
                  id: 'customers.domain.maritalStatus.married',
                }),
                value: 'MARRIED',
              },
              {
                label: intl.formatMessage({
                  id: 'customers.domain.maritalStatus.divorced',
                }),
                value: 'DIVORCED',
              },
              {
                label: intl.formatMessage({
                  id: 'customers.domain.maritalStatus.widower',
                }),
                value: 'WIDOWER',
              },
            ]}
          ></Select>
        </FloatingFormItem>
      </Col>

      <Col xs={24} sm={12} lg={8}>
        <FloatingFormItem
          name="profession"
          label={intl.formatMessage({ id: 'customers.domain.profession' })}
          rules={[
            {
              pattern: /^[a-zA-Z\u00C0-\u00FF ]+/i,
              message: intl.formatMessage({
                id: 'commons.form.errors.invalidField',
              }),
            },
            defaultMaxLenghtFieldError(50),
          ]}
        >
          <Input />
        </FloatingFormItem>
      </Col>
    </>
  );
}

export default NaturalPersonFields;
